/**
 * Read-only IndexedDB helper for vulnerability detection dates.
 * Used when an older list API omits `date_time` but dates were cached earlier.
 */

import { findingDetectedMs } from './overviewDateFilter';

const DB_NAME = 'xspectre-vuln-dates';
const DB_VERSION = 1;
const STORE = 'dates';

let dbPromise = null;

function openDb() {
  if (typeof indexedDB === 'undefined') return Promise.resolve(null);
  if (dbPromise) return dbPromise;
  dbPromise = new Promise((resolve, reject) => {
    const req = indexedDB.open(DB_NAME, DB_VERSION);
    req.onupgradeneeded = () => {
      const db = req.result;
      if (!db.objectStoreNames.contains(STORE)) {
        db.createObjectStore(STORE);
      }
    };
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error || new Error('IndexedDB open failed'));
  }).catch(() => null);
  return dbPromise;
}

function idbGetMany(db, keys) {
  if (!db || !keys.length) return Promise.resolve(new Map());
  return new Promise((resolve) => {
    const out = new Map();
    const tx = db.transaction(STORE, 'readonly');
    const store = tx.objectStore(STORE);
    let pending = keys.length;
    keys.forEach((key) => {
      const req = store.get(String(key));
      req.onsuccess = () => {
        if (req.result !== undefined) out.set(String(key), req.result);
        pending -= 1;
        if (pending <= 0) resolve(out);
      };
      req.onerror = () => {
        pending -= 1;
        if (pending <= 0) resolve(out);
      };
    });
    tx.oncomplete = () => resolve(out);
  });
}

export function countDatedFindings(rows) {
  let dated = 0;
  for (const row of rows || []) {
    if (!Number.isNaN(findingDetectedMs(row))) dated += 1;
  }
  return dated;
}

/** Merge cached dates onto rows. Does not hit the network. */
export async function applyCachedDetectionDates(rows) {
  const list = Array.isArray(rows) ? rows : [];
  if (!list.length) return { rows: list, coverage: 1, missingIds: [] };
  const db = await openDb();
  if (!db) return { rows: list, coverage: 0, missingIds: list.map((r) => r?.vuln_id).filter(Boolean) };

  const ids = list.map((r) => String(r?.vuln_id || '')).filter(Boolean);
  const cached = await idbGetMany(db, ids);
  const missingIds = [];
  const merged = list.map((row) => {
    const id = String(row?.vuln_id || '');
    if (!id) return row;
    if (!Number.isNaN(findingDetectedMs(row))) return row;
    if (cached.has(id)) {
      const date_time = cached.get(id);
      return date_time ? { ...row, date_time } : row;
    }
    missingIds.push(id);
    return row;
  });
  const dated = countDatedFindings(merged);
  const coverage = list.length ? dated / list.length : 1;
  return { rows: merged, coverage, missingIds };
}
